/**
 * CR-7781: the Haulage Cost field as the customer reads it — with a Change
 * control when the customer may change the cost itself. CR-8613: every other
 * haulage field too (pickup date, agreed delivery date, lorry number) and the
 * customer's own Expected Pickup Date, behind the same Admin Setup switch and
 * the same rules — the only way to change that date once Edit Call-Off closes
 * at Dispatched.
 *
 * Same pattern as the Haulier field ({@link CallOffHaulierField}): the value
 * reads like the other haulage fields, and "Change" swaps it for the editor in
 * place, in its own cell. The reason follows the call-off's other
 * reason-for-change form ({@link StockCorrectionDialog}): marked required,
 * with an example and a character count. Shown editable only while the server
 * says so (`can_customer_edit_haulage_cost`: the customer's Admin Setup switch
 * is on and the call-off was not cancelled). The audit history keeps the
 * reason beside the old and new value.
 *
 * @link CallOffHaulagePanel for the host.
 */
import { useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, CustomerHaulageField } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Loader2, Pencil } from 'lucide-react';
import { toast } from 'sonner';

/** The server's limit on the reason (CustomerHaulageCostSerializer). */
const REASON_MAX = 500;

/** Matches the read-only labels beside it, in both states. */
const LABEL_CLASS = 'block text-sm font-medium text-muted-foreground mb-1';

interface FieldConfig {
  label: string;
  /** Lower-case, for the toasts and the button's accessible name. */
  noun: string;
  inputType: 'number' | 'text' | 'date';
  /** The server's length limit, for a text value. */
  maxLength?: number;
  reasonPlaceholder: string;
  /** Same value? Compared the way the server will compare it. */
  same: (a: string, b: string) => boolean;
  save: (id: number, value: string, reason: string) => Promise<unknown>;
}

/** A date field: same day, same value; saved through the shared endpoint. */
const dateField = (
  field: CustomerHaulageField, label: string, noun: string, reasonPlaceholder: string,
): FieldConfig => ({
  label, noun, inputType: 'date', reasonPlaceholder,
  same: (a, b) => a.slice(0, 10) === b.slice(0, 10),
  save: (id, value, reason) => callOffAPI.saveCustomerHaulageValue(id, { field, value, reason }),
});

const FIELDS: Record<CustomerHaulageField, FieldConfig> = {
  pickup_date: dateField(
    'pickup_date', 'Expected Pickup Date', 'expected pickup date',
    'e.g. The supplier can only load on the Thursday',
  ),
  haulage_cost: {
    label: 'Haulage Cost',
    noun: 'haulage cost',
    inputType: 'number',
    reasonPlaceholder: 'e.g. Fuel surcharge agreed with the haulier by phone',
    // "450" and "450.00" are one cost, not a change.
    same: (a, b) => (a === '' || b === '' ? a === b : Number(a) === Number(b)),
    save: (id, value, reason) => callOffAPI.saveCustomerHaulageCost(id, {
      haulage_cost: value === '' ? null : value, reason,
    }),
  },
  haulier_pickup_date: dateField(
    'haulier_pickup_date', 'Pickup Date', 'pickup date',
    'e.g. The haulier moved the collection to the next morning',
  ),
  haulier_agreed_delivery_date: dateField(
    'haulier_agreed_delivery_date', 'Agreed Delivery Date', 'agreed delivery date',
    'e.g. Delivery agreed for Monday with the warehouse',
  ),
  lorry_number: {
    label: 'Lorry Number',
    noun: 'lorry number',
    inputType: 'text',
    maxLength: 50,
    reasonPlaceholder: 'e.g. The haulier sent a different lorry on the day',
    // The server trims it, as it does the haulier's own entry.
    same: (a, b) => a.trim() === b.trim(),
    save: (id, value, reason) => callOffAPI.saveCustomerHaulageValue(id, {
      field: 'lorry_number', value: value.trim(), reason,
    }),
  },
};

interface CallOffCustomerHaulageCostProps {
  callOff: CallOff;
  /** Which value this cell shows; the haulage cost unless told otherwise. */
  field?: CustomerHaulageField;
  /** Reload the call-off and its history after a save. */
  onSaved: () => void;
}

export default function CallOffCustomerHaulageCost({
  callOff, field = 'haulage_cost', onSaved,
}: CallOffCustomerHaulageCostProps) {
  const config = FIELDS[field];
  const { formatDate } = useFormatters();
  const saved = callOff[field] ?? '';
  const shown = saved && config.inputType === 'date' ? formatDate(saved) : saved;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState<string>(saved);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  // A refusal has to survive the toast, as on the Haulier field.
  const [error, setError] = useState<string | null>(null);

  const changeable = callOff.can_customer_edit_haulage_cost === true;
  const trimmedReason = reason.trim();
  const inputId = `customer-${field.replace(/_/g, '-')}`;
  const Noun = config.noun.charAt(0).toUpperCase() + config.noun.slice(1);

  const startEditing = () => {
    setValue(config.inputType === 'date' ? saved.slice(0, 10) : saved);
    setReason('');
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await config.save(callOff.id, value, trimmedReason);
      toast.success(value.trim() === '' ? `${Noun} removed` : `${Noun} updated`);
      setEditing(false);
      onSaved();
    } catch (err: unknown) {
      const message = getApiErrorMessage(err, `Failed to update ${config.noun}`);
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="space-y-2">
        <div>
          <Label htmlFor={inputId} className={LABEL_CLASS}>{config.label}</Label>
          <Input
            id={inputId}
            type={config.inputType}
            {...(config.inputType === 'number' ? { min: '0', step: '0.01' } : {})}
            maxLength={config.maxLength}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={saving}
            autoFocus
          />
        </div>
        <div>
          <Label htmlFor={`${inputId}-reason`} className={LABEL_CLASS}>
            Reason for change <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id={`${inputId}-reason`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={config.reasonPlaceholder}
            maxLength={REASON_MAX}
            rows={3}
            disabled={saving}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {trimmedReason.length}/{REASON_MAX} characters
          </p>
        </div>
        {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || config.same(value, saved) || trimmedReason === ''}
          >
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <label className={LABEL_CLASS}>{config.label}</label>
      <div className="flex items-center gap-2 border border-border rounded-lg bg-muted/30 px-3 py-2 h-[42px]">
        <span className={`flex-1 truncate font-medium ${saved ? '' : 'text-muted-foreground'}`}>
          {shown || 'Not entered'}
        </span>
        {changeable && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2"
            onClick={startEditing}
            aria-label={`Change ${config.noun}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1" />
            Change
          </Button>
        )}
      </div>
    </div>
  );
}
